import type {
  TimedContourSample,
  TimedSampleQuery,
  TimedSampleSource,
} from "@carbon6/c6-frontend-design/annotation";

export class AnnotationTimedContourSource implements TimedSampleSource<TimedContourSample> {
  private readonly listeners = new Set<() => void>();
  private readonly samples: TimedContourSample[] = [];

  constructor(private readonly capacity: number) {
    if (!Number.isInteger(capacity) || capacity <= 0) {
      throw new RangeError("contour sample capacity must be a positive integer");
    }
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSampleAt(query: TimedSampleQuery): TimedContourSample | null {
    if (this.samples.length === 0) return null;
    const tolerance = query.toleranceSeconds;
    if (query.sampling === "hold") {
      for (let index = this.samples.length - 1; index >= 0; index -= 1) {
        const sample = this.samples[index];
        if (sample !== undefined && sample.timestampSeconds <= query.timeSeconds) {
          return tolerance === undefined || query.timeSeconds - sample.timestampSeconds <= tolerance
            ? sample
            : null;
        }
      }
      return null;
    }

    let nearest: TimedContourSample | null = null;
    let distance = Number.POSITIVE_INFINITY;
    for (const sample of this.samples) {
      const nextDistance = Math.abs(sample.timestampSeconds - query.timeSeconds);
      if (nextDistance < distance) {
        nearest = sample;
        distance = nextDistance;
      }
    }
    if (query.sampling === "exact" && distance !== 0 && tolerance === undefined) return null;
    return tolerance === undefined || distance <= tolerance ? nearest : null;
  }

  publish(sample: TimedContourSample): void {
    const existing = this.samples.findIndex(
      (candidate) => candidate.timestampSeconds === sample.timestampSeconds,
    );
    if (existing >= 0) this.samples.splice(existing, 1);
    const insertion = this.samples.findIndex(
      (candidate) => candidate.timestampSeconds > sample.timestampSeconds,
    );
    if (insertion < 0) this.samples.push(sample);
    else this.samples.splice(insertion, 0, sample);
    while (this.samples.length > this.capacity) this.samples.shift();
    for (const listener of this.listeners) listener();
  }

  clear(): void {
    if (this.samples.length === 0) return;
    this.samples.length = 0;
    for (const listener of this.listeners) listener();
  }

  get size(): number {
    return this.samples.length;
  }
}
